import { useMemo } from "react";

import { DatePicker, Select } from "antd";
import {
  ArpFields,
  ArpsDesignerKind,
  TypeWellInputTableMode
} from "constants/forecasts.constants";
import moment from "moment";
import styled from "styled-components";

import { ArpInputField } from ".";
import { ArpInputParams } from "../../models/UserArpsModel";
import { ProductTypeEnum } from "../arps/models/ArpsSegment";
import { SegmentTemplate } from "../arps/models/SegmentTemplate";
import { getHeaderUnits } from "../arps/utils/arpsUtils";
import { getAbbreviatedSegmentLabel } from "../arps/utils/typeWellUtils";

const ArpInput = (params: ArpInputParams) => {
  const {
    header,
    product,
    mode,
    segmentData,
    isCalculated,
    isForecastConstant,
    isRatio,
    isSync,
    handleReload,
    handleReset,
    handleSave,
    onChange,
    typeWellTemplates,
    productSegmentTemplateName,
    fieldExists,
    inputRef,
    onBlur,
    isDisabled,
    kind
  } = params;
  const isDateHeader = header === "Start Date" || header === "End Date";
  const rawDate = isDateHeader && segmentData ? segmentData[header] : null;
  // Keep the moment's identity stable across renders — antd's DatePicker
  // resets its open calendar view whenever `value` changes reference (EVA-78).
  const date = useMemo(
    // eslint-disable-next-line import/no-named-as-default-member
    () => (rawDate ? moment.utc(rawDate) : null),
    [rawDate]
  );
  try {
    const key = header + "-" + product;
    let val: number = segmentData && header in segmentData ? segmentData[header] : null;

    // For ratio products, get values from segmentData which should have Qo, Qi, Duration
    if (isRatio && isForecastConstant) {
      // segmentData should have the structure { Qo, Qi, Duration }
      val = segmentData[header];
    } else if (isForecastConstant && segmentData.value) {
      val = segmentData.value;
    }

    // Show "N/A" for Restricted EUR and Qf at Cutoff when value is undefined
    // Check this BEFORE component key check to ensure proper rendering
    if (
      (header === ArpFields.RestrictedEUR || header === ArpFields.QfAtCutoff) &&
      (val === undefined || val === null)
    ) {
      return (
        <NADisplay key={key + "-na"} data-testid={`${header}-na-${product}`}></NADisplay>
      );
    }

    // Component key is stable for all fields now
    const componentKey = key;

    // For ratio products, show all three fields (Qo, Qi, Duration)
    // For non-ratio constants, only show Qi field
    // For fields that don't exist in the product's template, show FillerDiv
    if (
      (isForecastConstant && !isRatio && header !== "Qi") ||
      (isForecastConstant && isRatio && !fieldExists) ||
      (!fieldExists && !isForecastConstant && header !== "Template") ||
      (header === ArpFields.RestrictedEUR && kind === ArpsDesignerKind.Forecasting) ||
      (header === ArpFields.QfAtCutoff && kind === ArpsDesignerKind.Forecasting)
    ) {
      return <FillerDiv key={componentKey} />;
    }

    if (header === "Template") {
      // Disable template selection for ratio products with segments (they use fixed "2 Seg." template)
      const isRatioWithSegments = isRatio && !isForecastConstant;

      return (
        <Select
          key={componentKey}
          data-testid={componentKey + "-select"}
          value={productSegmentTemplateName}
          placeholder="Template"
          disabled={isDisabled || isRatioWithSegments}
          onChange={(val) => {
            onChange && onChange(val);
          }}
          options={typeWellTemplates.map((item: SegmentTemplate) => ({
            label: getAbbreviatedSegmentLabel(item.name),
            value: item.name
          }))}
        />
      );
    }

    if (header === "Start Date" || header === "End Date") {
      const testIdPrefix = header === "Start Date" ? "start-date" : "end-date";
      return (
        <DatePicker
          data-testid={`${testIdPrefix}-picker-${product}`}
          allowClear={false}
          value={date}
          disabled={
            isDisabled ||
            mode === TypeWellInputTableMode.Segment ||
            (isRatio && !isForecastConstant) ||
            isSync ||
            header === "End Date"
          }
          key={componentKey}
          onChange={(evt) => onChange && onChange(evt?.toDate())}
          popupClassName={`${testIdPrefix}-picker-popup-${product}`}
        />
      );
    }

    return (
      <ArpInputField
        header={header}
        globalRef={inputRef}
        key={componentKey}
        min={header.startsWith("t") || header === "maxRate" ? 0 : undefined} // t = times for ramp-up or constrained templates.
        type="number"
        isCalculated={isCalculated}
        unit={getHeaderUnits(header, product, mode)}
        disabled={
          isDisabled ||
          mode === TypeWellInputTableMode.Segment ||
          header === ArpFields.CTD ||
          header === ArpFields.RestrictedEUR ||
          header === ArpFields.QfAtCutoff ||
          isSync ||
          // "EUR" in forecasting widget is "Remaining"
          (header === ArpFields.EUR && kind === ArpsDesignerKind.Forecasting)
        }
        step={header.startsWith("B") ? 0.1 : 1}
        value={val != null ? Number(val) : undefined}
        handleReload={handleReload}
        handleReset={handleReset}
        handleSave={handleSave}
        onBlur={onBlur}
        onChange={(value) => {
          if (isNaN(value)) {
            value = 0;
          }
          onChange && onChange(value);
        }}
        dataProduct={ProductTypeEnum[product]}
        kind={kind}
      />
    );
  } catch (err) {
    return <div key={header + "-" + product}>Error {err}</div>;
  }
};
const FillerDiv = styled.div`
  min-height: 27px;
`;

const NADisplay = styled.div`
  min-height: 27px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 11px;
  color: rgba(0, 0, 0, 0.25);
  font-style: italic;
  width: 100%;
  position: relative;
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  cursor: not-allowed;
`;

export default ArpInput;
